/**
 * Мастер добавления и переподключения Telegram-аккаунтов (KAN-601).
 * Tabs на четыре схемы (KAN-677: архив от продавца с автоопределением
 * содержимого стоит перед сессией вручную); режим relogin: номер
 * предзаполнен, только схемы по коду.
 * Перед работой запрашивается статус держателя сессий: если он недоступен,
 * мастер блокируется. Состояние форм живёт внутри DialogContent и сбрасывается
 * при закрытии (секреты не остаются в DOM).
 * KAN-648: общие для всех схем поля видеозаписи (ссылка и файл); после
 * подключения ссылка уходит PATCH-ем, файл загружается на {id}/video/.
 * KAN-676: видео-поля передаются в форму схемы слотом (videoFields), чтобы
 * кнопка подключения была последней; состояние остаётся в мастере и
 * переживает переключение вкладок. Строка вкладок переносится (четыре
 * вкладки шире модалки max-w-lg), тело диалога с min-w-0, иначе grid-элемент
 * растягивался под min-content вкладок и поля уходили за край модалки.
 * KAN-740: вкладка "Архив от продавца (zip)" первая и открыта по умолчанию.
 * KAN-752: если в кабинете несколько кампаний Telegram-рассылки (операторов
 * канала telegram_user), новый аккаунт подключается к выбранной кампании:
 * пока она не выбрана, схемы подключения не показываются. Переподключение
 * идёт к оператору аккаунта, выбора нет.
 */
import { useCallback, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  ConnectCodeForm,
  type ReloginTarget,
} from '@/components/telegram/ConnectCodeForm';
import { AccountVideoFields } from '@/components/telegram/AccountVideoFields';
import { ConnectWarnings } from '@/components/telegram/ConnectWarnings';
import { ImportArchiveForm } from '@/components/telegram/ImportArchiveForm';
import { ImportSessionForm } from '@/components/telegram/ImportSessionForm';
import { CampaignSelect } from '@/components/telegram/CampaignSelect';
import { useChannelOperators } from '@/hooks/useChannelOperators';
import { TELEGRAM_USER_CHANNEL } from '@/services/api/channelOperators';
import {
  EMPTY_VIDEO_DRAFT,
  connectedAccountId,
  saveVideoDraft,
  type VideoDraft,
} from '@/lib/accountVideo';
import { SCHEME_LABELS, describeApiError } from '@/lib/telegramConnect';
import { telegramAccountsApi } from '@/services/api/telegramAccounts';
import type { ConnectScheme, TelegramAccount } from '@/types/telegramAccount';

export const CAMPAIGN_CHOICE_HINT =
  'Аккаунт будет рассылать сообщения этой кампании.';

export const GATEWAY_UNAVAILABLE_TEXT =
  'Держатель сессий недоступен, подключение невозможно сейчас';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Переподключение существующего аккаунта (intent relogin). */
  relogin?: ReloginTarget;
}

function Body({
  relogin,
  onClose,
}: {
  relogin?: ReloginTarget;
  onClose: () => void;
}): JSX.Element {
  const queryClient = useQueryClient();
  const [scheme, setScheme] = useState<ConnectScheme>(
    relogin ? 'code' : 'archive'
  );
  const [locked, setLocked] = useState(false);
  const [video, setVideo] = useState<VideoDraft>(EMPTY_VIDEO_DRAFT);
  const [operatorId, setOperatorId] = useState('');
  const campaigns = useChannelOperators(TELEGRAM_USER_CHANNEL, !relogin);
  const campaignRequired = !relogin && campaigns.choiceRequired;
  const chosenOperator = campaignRequired ? operatorId || undefined : undefined;

  const { data, isLoading, isError } = useQuery({
    queryKey: ['telegram-gateway-status'],
    queryFn: telegramAccountsApi.gatewayStatus,
    retry: false,
    staleTime: 0,
  });

  const handleConnected = useCallback(
    (account: TelegramAccount) => {
      queryClient.invalidateQueries({ queryKey: ['telegram-accounts'] });
      const accountId = connectedAccountId(account);
      if (!accountId || (!video.url.trim() && !video.file)) return;
      const ownerId =
        account.operator_id || relogin?.operatorId || chosenOperator || undefined;
      saveVideoDraft(telegramAccountsApi, accountId, video, ownerId)
        .then(() => {
          toast.success('Видео аккаунта сохранено');
          queryClient.invalidateQueries({ queryKey: ['telegram-accounts'] });
        })
        .catch((err: unknown) =>
          toast.error(describeApiError(err, 'Аккаунт подключён, но видео не сохранено'))
        );
    },
    [queryClient, video, relogin, chosenOperator]
  );

  if (isLoading || campaigns.isLoading) {
    return (
      <div className="flex justify-center py-8" data-testid="gateway-loading">
        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
      </div>
    );
  }

  if (isError || !data?.available) {
    return (
      <Alert variant="destructive" data-testid="gateway-unavailable">
        <AlertDescription>{GATEWAY_UNAVAILABLE_TEXT}</AlertDescription>
      </Alert>
    );
  }

  const schemes: ConnectScheme[] = relogin
    ? ['code_2fa', 'code']
    : ['archive', 'code_2fa', 'code', 'session'];

  // Один элемент на все схемы: активна одна вкладка, остальные размонтированы
  const videoFields = (
    <AccountVideoFields
      value={video}
      onChange={setVideo}
      disabled={locked}
      idPrefix="connect-video"
    />
  );

  const campaignField = campaignRequired && (
    <CampaignSelect
      operators={campaigns.operators}
      value={operatorId}
      onChange={setOperatorId}
      disabled={locked}
      hint={CAMPAIGN_CHOICE_HINT}
    />
  );

  if (campaignRequired && !operatorId) {
    return (
      <div className="min-w-0 space-y-4" data-testid="connect-body">
        {campaignField}
      </div>
    );
  }

  return (
    <div className="min-w-0 space-y-4" data-testid="connect-body">
      {campaignField}
      <ConnectWarnings scheme={scheme} relogin={!!relogin} />
      <Tabs value={scheme} onValueChange={(v) => setScheme(v as ConnectScheme)}>
        <TabsList className="flex h-auto w-full flex-wrap justify-start">
          {schemes.map((s) => (
            <TabsTrigger
              key={s}
              value={s}
              disabled={locked}
              data-testid={`connect-tab-${s}`}
            >
              {SCHEME_LABELS[s]}
            </TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="code_2fa">
          <ConnectCodeForm
            scheme="code_2fa"
            relogin={relogin}
            onLockChange={setLocked}
            onConnected={handleConnected}
            onClose={onClose}
            videoFields={videoFields}
            operatorId={chosenOperator}
          />
        </TabsContent>
        <TabsContent value="code">
          <ConnectCodeForm
            scheme="code"
            relogin={relogin}
            onLockChange={setLocked}
            onConnected={handleConnected}
            onClose={onClose}
            videoFields={videoFields}
            operatorId={chosenOperator}
          />
        </TabsContent>
        {!relogin && (
          <TabsContent value="archive">
            <ImportArchiveForm
              onConnected={handleConnected}
              onClose={onClose}
              videoFields={videoFields}
              operatorId={chosenOperator}
            />
          </TabsContent>
        )}
        {!relogin && (
          <TabsContent value="session">
            <ImportSessionForm
              onConnected={handleConnected}
              onClose={onClose}
              videoFields={videoFields}
              operatorId={chosenOperator}
            />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}

export function ConnectAccountDialog({
  open,
  onOpenChange,
  relogin,
}: Props): JSX.Element {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="connect-account-dialog"
        className="max-h-[90vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle>
            {relogin ? 'Переподключить аккаунт' : 'Добавить аккаунт'}
          </DialogTitle>
          <DialogDescription>
            Подключение аккаунта Telegram к рассылке.
          </DialogDescription>
        </DialogHeader>
        <Body relogin={relogin} onClose={() => onOpenChange(false)} />
      </DialogContent>
    </Dialog>
  );
}
